// 매장 상설 QR 버튼 (CRM 헤더, "폼 바로 열기" 왼쪽).
//
// 누르면 매장 상설 QR(kiosk 토큰)을 모달에 띄운다. 손님이 그 QR을 스캔하면 그 자리에서
// 새 신청서가 만들어진다(백엔드 kiosk 분기). "폼 바로 열기"가 고객 1명에게 1회용 링크를 여는
// 것과 달리, 이 QR은 매장 벽에 붙여 두고 여러 손님이 계속 쓴다.
//
// ── 같은 QR 고정 ──
// 서버가 kiosk 토큰 원문을 보관하므로(벽에 붙여 공개 전시하는 토큰이라 무방), 버튼을 누를
// 때마다 "매번 같은 QR"이 그대로 다시 뜬다. 활성 QR이 없으면 최초 1회 자동 발급한다.
//
// ── 재발급 ──
// 인쇄물을 새로 갈아야 할 때(분실·교체)만 모달 안의 "🔄 재발급"을 누른다. 재발급하면 직전
// QR은 즉시 무효화되므로 한 번 확인을 받는다. 옛 발급 건(원문 미보관)은 화면에 띄울 수 없어
// 재발급해야 복구되는데, 그 경우엔 모달이 재발급을 안내한다.
//
// 인쇄는 모달의 "🖨 출력" 버튼으로 한다.

import { useRef, useState } from "react";
import { fetchKioskFormStatus, issueKioskFormToken } from "../../api/crm";
import { useToast } from "../common/Toast";
import PersistentQrModal from "./PersistentQrModal";
import { useModal } from "../../hooks/useModal";

export default function KioskQrButton({
  className = "",
}: {
  className?: string;
}) {
  const toast = useToast();
  const [busy, setBusy] = useState(false);
  // 매장에 비치할 QR 을 띄우는 창 — 출력하러 다시 여는 일이 잦아 주소에 싣는다.
  const qrModal = useModal("store-kiosk-qr");
  const open = qrModal.isOpen;
  // 표시 중인 QR 의 평문 URL. open 인데 url 이 null 이면 "옛 발급 건이라 재발급 필요" 상태.
  const [url, setUrl] = useState<string | null>(null);
  const qrRef = useRef<HTMLDivElement>(null);

  const toUrl = (token: string) => `${window.location.origin}/form/${token}`;

  // 버튼 클릭 = QR 열기. 서버에 보관된 같은 QR 을 띄운다(없으면 최초 1회 자동 발급).
  const onOpen = async () => {
    if (busy) return;
    setBusy(true);
    try {
      const status = await fetchKioskFormStatus();
      if (status.active && status.token) {
        setUrl(toUrl(status.token)); // 매번 같은 QR
      } else if (status.active && !status.token) {
        setUrl(null); // 옛 발급 건 — 원문 없음 → 모달에서 재발급 안내
      } else {
        const res = await issueKioskFormToken(); // 활성 QR 없음 → 최초 1회 발급
        setUrl(toUrl(res.token));
      }
      qrModal.openModal();
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`매장 QR 불러오기 실패: ${msg}`);
    } finally {
      setBusy(false);
    }
  };

  // 모달 안 "🔄 재발급" — 새 토큰을 만들고 직전 QR(인쇄물)은 무효화한다.
  const onReissue = async () => {
    if (
      !window.confirm(
        "새로 발급하면 기존에 붙여 둔 QR은 더 이상 작동하지 않습니다.\n계속할까요?",
      )
    ) {
      return;
    }
    try {
      const res = await issueKioskFormToken();
      setUrl(toUrl(res.token));
      toast.success("매장 QR을 새로 발급했습니다. 출력해 다시 비치하세요.");
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      toast.error(`매장 QR 재발급 실패: ${msg}`);
    }
  };

  // 모달 안 "🖨 출력" — 화면에 보이는 QR(SVG)을 인쇄 창에 그려 인쇄.
  // 직접 클릭이라 window.open 이 팝업 차단에 걸리지 않는다.
  const onPrint = () => {
    const svg = qrRef.current?.innerHTML;
    if (!svg || !url) return;
    const win = window.open("", "_blank", "width=480,height=640");
    if (!win) {
      toast.error("팝업이 차단됐습니다. 팝업을 허용한 뒤 다시 시도하세요.");
      return;
    }
    win.document.write(`<!doctype html><html><head><meta charset="utf-8">
<title>매장 입력 QR</title>
<style>
  body{font-family:-apple-system,system-ui,sans-serif;text-align:center;margin:0;padding:44px 24px;color:#111}
  h1{font-size:23px;margin:0 0 8px}
  p{font-size:15px;color:#444;margin:4px 0 28px;line-height:1.5}
  .qr{display:inline-block;padding:18px;border:1px solid #e5e7eb;border-radius:14px}
  .url{margin-top:22px;font-size:11px;color:#9ca3af;word-break:break-all}
</style></head>
<body>
  <h1>휴대폰 개통 신청서 작성</h1>
  <p>아래 QR을 휴대폰 카메라로 스캔해<br/>신청 정보를 직접 입력해 주세요.</p>
  <div class="qr">${svg}</div>
  <div class="url">${url}</div>
  <script>window.onload=function(){window.print();}</script>
</body></html>`);
    win.document.close();
  };

  return (
    <>
      <button
        type="button"
        onClick={onOpen}
        disabled={busy}
        className={className}
        title="매장에 비치할 상설 입력 QR을 띄웁니다"
      >
        {busy ? "불러오는 중…" : "🖨 매장 QR"}
      </button>

      <PersistentQrModal
        isOpen={open}
        onClose={qrModal.closeModal}
        title="매장 상설 QR"
        subtitle="손님이 스캔해 입력을 시작하면 새 신청서가 만들어집니다. 출력해 매장에 비치하세요."
        url={url}
        qrRef={qrRef}
        unavailableMessage={
          <p className="max-w-xs text-center text-sm text-gray-500">
            기존 QR은 화면에 다시 띄울 수 없습니다.
            <br />
            아래 <span className="font-medium">🔄 재발급</span>을 눌러 새 QR을
            만든 뒤 출력해 비치하세요.
          </p>
        }
        actions={[
          {
            label: "🔄 재발급",
            onClick: onReissue,
            tone: "outline",
            loadingLabel: "발급 중…",
          },
        ]}
        confirmLabel="🖨 출력"
        onConfirm={onPrint}
        confirmDisabled={url == null}
      />
    </>
  );
}
